import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { fetchRefCode } from "../utils/api";
import ReCaptcha from "../components/ReCaptcha";

const RefCodeForm = () => {
  const [email, setEmail] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: "", text: "" });
  const [copied, setCopied] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [showRegisterLink, setShowRegisterLink] = useState(false);

  const baseUrl =
    process.env.REACT_APP_BASE_URL || "https://yuvachangemakers.com";
  const shareLink = referralCode ? `${baseUrl}/register/${referralCode}` : "";

  // Production-grade email validation regex
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMsg({ type: "", text: "" });

    if (!email) {
      setStatusMsg({ type: "error", text: "Please enter your email address." });
      return;
    }
    if (!validateEmail(email)) {
      setStatusMsg({
        type: "error",
        text: "Please enter a valid email format.",
      });
      return;
    }

    if (!captchaToken) {
      setCaptchaError("Please complete the captcha verification");
      setStatusMsg({
        type: "error",
        text: "Please complete the captcha verification.",
      });
      return;
    }

    setLoading(true);
    setShowRegisterLink(false);
    try {
      const response = await fetchRefCode(email, captchaToken);

      setReferralCode(response?.referralCode || response?.data?.code || "");
      setStatusMsg({
        type: "success",
        text: `${response?.data?.message || "Referral code fetched successfully!"}`,
      });
    } catch (err) {
      const errorDetail =
        err?.response?.data?.data?.message ||
        err?.response?.data?.message ||
        "Failed to fetch code. Please try again.";

      // Check if user is not registered
      const isNotRegistered = errorDetail.toLowerCase().includes("not registered");
      setShowRegisterLink(isNotRegistered);

      setStatusMsg({ type: "error", text: errorDetail });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#FFF9F0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "24px",
            p: { xs: 3, md: 6 },
            textAlign: "center",
            boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.05)",
            border: "1px solid #F7F1E8",
          }}
        >
          <img
            src="/assets/popup.jpg"
            alt="Thank You"
            style={{
              maxWidth: "100%",
              height: "auto",
              borderRadius: "16px",
              marginBottom: "32px",
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "24px", md: "32px" },
              fontFamily: "Test Domaine Display, serif",
              fontWeight: 700,
              color: "#000",
              lineHeight: 1.3,
              mb: 3,
            }}
          >
            Get Your Referral Code
          </Typography>

          {!referralCode ? (
            /* --- EMAIL FORM SECTION --- */
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxWidth: "450px",
                mx: "auto",
              }}
            >
              <TextField
                fullWidth
                placeholder="Enter your registered email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f9f9f9",
                    height: "56px", // Optimal height for iOS touch targets
                  },
                }}
              />
              <ReCaptcha
                onVerify={(token) => {
                  setCaptchaToken(token);
                  setCaptchaError("");
                }}
                onExpire={() => {
                  setCaptchaToken(null);
                  setCaptchaError("Captcha expired. Please verify again.");
                }}
                onError={() => {
                  setCaptchaToken(null);
                  setCaptchaError("Captcha error. Please try again.");
                }}
                error={captchaError}
              />
              <Button
                type="submit"
                disabled={loading}
                sx={{
                  backgroundColor: "#333",
                  color: "#fff",
                  height: "56px",
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: "18px",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#000" },
                  "&.Mui-disabled": { backgroundColor: "#999" },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Fetch My Code →"
                )}
              </Button>
              {statusMsg.text && (
                <Alert
                  severity={statusMsg.type}
                  sx={{ borderRadius: "12px", textAlign: "left" }}
                >
                  {statusMsg.text}
                </Alert>
              )}
              {showRegisterLink && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#666",
                      mb: 1,
                    }}
                  >
                    Don't have an account yet?
                  </Typography>
                  <Button
                    component={Link}
                    to="/register"
                    sx={{
                      backgroundColor: "#87EA5C",
                      color: "#000",
                      fontWeight: 600,
                      px: 4,
                      py: 1,
                      borderRadius: "50px",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#75d84a" },
                    }}
                  >
                    Register Now →
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            /* --- SUCCESS / REFERRAL SECTION --- */
            <>
              <Box
                sx={{
                  backgroundColor: "#FFF9F0",
                  borderRadius: "12px",
                  p: 3,
                  my: 3,
                  border: "1px dashed #87EA5C",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "18px", md: "20px" },
                    fontFamily: "Elza, sans-serif",
                    fontWeight: 600,
                    color: "#333",
                    mb: 2,
                  }}
                >
                  Your unique referral code
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "32px", md: "48px" },
                    fontFamily: "Elza, sans-serif",
                    fontWeight: 800,
                    color: "#87EA5C",
                    letterSpacing: "4px",
                    mb: 1,
                  }}
                >
                  {referralCode}
                </Typography>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "16px", md: "18px" },
                    fontFamily: "Elza, sans-serif",
                    fontWeight: 600,
                    color: "#000",
                    mb: 2,
                  }}
                >
                  Share this link to spread the movement!
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontFamily: "monospace",
                      color: "#666",
                      backgroundColor: "#f5f5f5",
                      p: 2,
                      borderRadius: "8px",
                      wordBreak: "break-all",
                      flex: 1,
                      width: "100%",
                      border: "1px solid #eee",
                    }}
                  >
                    {shareLink}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleCopyLink}
                    sx={{
                      backgroundColor: "#87EA5C",
                      color: "#000",
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: "8px",
                      minWidth: "140px",
                      "&:hover": { backgroundColor: "#75d84a" },
                    }}
                  >
                    {copied ? "Copied!" : "Copy Link"}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default RefCodeForm;
